import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Clock, CheckCircle, XCircle } from 'lucide-react';
import AnimatronicReview from './AnimatronicReview';
import MediaEditReview from './MediaEditReview';

const SubmissionList: React.FC = () => {
  const [selectedAnimatronicSubmission, setSelectedAnimatronicSubmission] = useState<string | null>(null);
  const [selectedMediaSubmission, setSelectedMediaSubmission] = useState<string | null>(null);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get media edit submissions that are visible to admin
      const { data: mediaEdits, error: mediaError } = await supabase
        .from('media_edit_submissions')
        .select(`
          *,
          user:user_profiles(
            display_name,
            avatar_url,
            is_deleted
          ),
          animatronic:animatronics(
            name,
            park,
            attraction
          )
        `)
        .eq('admin_view', true);

      if (mediaError) throw mediaError;

      // Get new animatronic submissions that are visible to admin
      const { data: newSubmissions, error: submissionError } = await supabase
        .from('animatronic_submissions')
        .select(`
          *,
          user:user_profiles(
            display_name,
            avatar_url,
            is_deleted
          )
        `)
        .eq('admin_view', true);

      if (submissionError) throw submissionError;

      // Format media edit submissions
      const formattedMediaEdits = (mediaEdits || []).map(edit => ({
        id: edit.id,
        type: 'media_edit',
        name: edit.animatronic?.name || 'Unknown Animatronic',
        status: edit.approved ? 'approved' : 'pending',
        user: edit.user,
        created_at: edit.created_at,
        animatronic_details: {
          name: edit.animatronic?.name,
          park: edit.animatronic?.park,
          attraction: edit.animatronic?.attraction
        }
      }));

      // Format new animatronic submissions
      const formattedNewSubmissions = (newSubmissions || []).map(sub => ({
        id: sub.id,
        type: 'new_animatronic',
        name: sub.name,
        status: sub.approved ? 'approved' : 'pending',
        user: sub.user,
        created_at: sub.created_at,
        animatronic_details: {
          name: sub.name,
          park: sub.park,
          attraction: sub.attraction
        }
      }));

      // Combine and sort all submissions by date
      const allSubmissions = [...formattedMediaEdits, ...formattedNewSubmissions]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setSubmissions(allSubmissions);
    } catch (err) {
      console.error('Error fetching submissions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load submissions');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  if (selectedAnimatronicSubmission) {
    return (
      <AnimatronicReview
        submissionId={selectedAnimatronicSubmission}
        onReviewComplete={() => {
          setSelectedAnimatronicSubmission(null);
          fetchSubmissions();
        }}
      />
    );
  }

  if (selectedMediaSubmission) {
    return (
      <MediaEditReview
        submissionId={selectedMediaSubmission}
        onReviewComplete={() => {
          setSelectedMediaSubmission(null);
          fetchSubmissions();
        }}
      />
    );
  }

  if (isLoading) {
    return <div className="text-center py-8">Loading submissions...</div>;
  }

  if (error) {
    return (
      <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-content-primary">Submissions</h2>
      
      <div className="grid gap-4">
        {submissions.length === 0 ? (
          <div className="text-center py-12 text-content-secondary">
            No submissions found
          </div>
        ) : (
          submissions.map((submission) => (
            <div
              key={submission.id}
              className="bg-background-secondary p-4 rounded-lg border border-gray-800 hover:border-brand/50 transition-all cursor-pointer"
              onClick={() => {
                if (submission.type === 'media_edit') {
                  setSelectedMediaSubmission(submission.id);
                } else {
                  setSelectedAnimatronicSubmission(submission.id);
                }
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-content-primary">
                    {submission.name}
                  </h3>
                  <p className="text-sm text-content-secondary">
                    {submission.animatronic_details?.park} - {submission.animatronic_details?.attraction}
                  </p>
                  <p className="text-sm text-content-secondary">
                    Submitted by {submission.user?.is_deleted ? 'Deleted Account' : (submission.user?.display_name || 'Unknown User')} on{' '}
                    {new Date(submission.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-2">
                    {getStatusIcon(submission.status)}
                    <span className="capitalize">{submission.status}</span>
                  </span>
                  <span className="px-3 py-1 bg-brand/10 text-brand rounded-full text-sm">
                    {submission.type === 'media_edit' ? 'Media Update' : 'New Animatronic'}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SubmissionList;
